import React, { useState, useEffect } from 'react';

const fmt = n => '$' + Number(n).toLocaleString('en-CA');

const s = {
  wrap: {
    fontFamily: 'Inter, system-ui, sans-serif',
    margin: '24px 0',
    borderRadius: '14px',
    overflow: 'hidden',
    border: '1px solid #e8edf2',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  },
  header: {
    background: '#0a2540',
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '10px' },
  headerTitle: { color: '#fff', fontWeight: '600', fontSize: '15px', margin: 0 },
  liveBadge: {
    background: '#00c853',
    color: '#fff',
    fontSize: '10px',
    fontWeight: '700',
    letterSpacing: '0.08em',
    padding: '3px 9px',
    borderRadius: '20px',
    textTransform: 'uppercase',
  },
  pendingBadge: {
    background: '#ff9800',
    color: '#fff',
    fontSize: '10px',
    fontWeight: '700',
    letterSpacing: '0.08em',
    padding: '3px 9px',
    borderRadius: '20px',
    textTransform: 'uppercase',
  },
  body: { background: '#fff', padding: '20px' },

  // Pricing grid
  pricingGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
    marginBottom: '16px',
  },
  priceCard: {
    background: '#f7f9fc',
    borderRadius: '10px',
    padding: '14px 16px',
    border: '1px solid #eef1f5',
  },
  priceCardHighlight: {
    background: '#e8f5e9',
    borderRadius: '10px',
    padding: '14px 16px',
    border: '1px solid #c8e6c9',
  },
  priceLabel: {
    fontSize: '11px',
    color: '#6b7c93',
    fontWeight: '600',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    marginBottom: '4px',
  },
  priceValue: { fontSize: '22px', fontWeight: '700', color: '#0a2540', lineHeight: 1.2 },
  priceValueGreen: { fontSize: '22px', fontWeight: '700', color: '#2e7d32', lineHeight: 1.2 },
  priceRange: { fontSize: '11px', color: '#8a9bb0', marginTop: '3px' },

  divider: { height: '1px', background: '#eef1f5', margin: '16px 0' },

  // Market intel row
  intelRow: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' },
  intelChip: {
    background: '#f0f4ff',
    border: '1px solid #dde6ff',
    borderRadius: '20px',
    padding: '5px 12px',
    fontSize: '12px',
    color: '#2d4aa8',
    fontWeight: '500',
  },
  demandHigh: {
    background: '#e8f5e9',
    border: '1px solid #c8e6c9',
    borderRadius: '20px',
    padding: '5px 12px',
    fontSize: '12px',
    color: '#2e7d32',
    fontWeight: '600',
  },
  demandMod: {
    background: '#fff8e1',
    border: '1px solid #ffecb3',
    borderRadius: '20px',
    padding: '5px 12px',
    fontSize: '12px',
    color: '#f57f17',
    fontWeight: '600',
  },
  demandLow: {
    background: '#fce4ec',
    border: '1px solid #f8bbd0',
    borderRadius: '20px',
    padding: '5px 12px',
    fontSize: '12px',
    color: '#c62828',
    fontWeight: '600',
  },
  summary: {
    fontSize: '13px',
    color: '#4a5568',
    lineHeight: 1.6,
    marginBottom: '16px',
    fontStyle: 'italic',
  },

  // CARFAX section
  carfaxWrap: {
    background: '#fff8f0',
    border: '1px solid #ffd6b0',
    borderRadius: '10px',
    padding: '14px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
  },
  carfaxLeft: {},
  carfaxTitle: { fontWeight: '700', color: '#0a2540', fontSize: '14px', marginBottom: '2px' },
  carfaxSub: { fontSize: '12px', color: '#6b7c93' },
  carfaxBtn: {
    background: '#E31837',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 18px',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-block',
    whiteSpace: 'nowrap',
    letterSpacing: '0.02em',
  },

  // Stolen check
  stolenWrap: {
    background: '#f3f0ff',
    border: '1px solid #d4c5ff',
    borderRadius: '10px',
    padding: '14px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
    marginTop: '10px',
  },
  stolenBtn: {
    background: '#5c35cc',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 18px',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-block',
    whiteSpace: 'nowrap',
  },

  poweredBy: { fontSize: '11px', color: '#b0bac5', marginTop: '14px', textAlign: 'right' },
  loading: { color: '#8a9bb0', fontSize: '14px', padding: '12px 0' },
  pending: { color: '#f57f17', fontSize: '13px', padding: '8px 0', fontWeight: '500' },
};

const demandStyle = d => {
  if (!d) return s.intelChip;
  if (d === 'High') return s.demandHigh;
  if (d === 'Low') return s.demandLow;
  return s.demandMod;
};

const MarketValueBadge = ({ vin, odometer, year, make, model, trim, province }) => {
  const [nhtsaData, setNhtsaData] = useState(null);
  const [compsData, setCompsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!vin) return;
    const km = odometer ? parseInt(odometer.replace(/\D/g, '')) : null;

    // NHTSA decode (always works)
    fetch(`/api/market-value?vin=${vin}`)
      .then(r => r.json())
      .then(d => {
        if (d.success) setNhtsaData(d);
      })
      .catch(() => {});

    // Market comps (needs year/make/model — from NHTSA or props)
    const y = year,
      mk = make,
      mo = model;
    if (y && mk && mo) {
      const params = new URLSearchParams({ year: y, make: mk, model: mo });
      if (trim) params.append('trim', trim);
      if (km) params.append('mileage', km);
      if (province) params.append('province', province);

      fetch(`/api/market-comps?${params}`)
        .then(r => r.json())
        .then(d => {
          setCompsData(d);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [vin, odometer, year, make, model, trim, province]);

  if (!vin) return null;

  const val = compsData?.valuation;
  const intel = compsData?.market_intelligence;
  // The endpoint now answers 503 (no provider configured) or 502 (provider
  // failed) rather than a 200 full of nulls, so "no valuation" is a state we
  // can describe accurately instead of guessing at.
  const unavailable = compsData?.success === false;
  const notConfigured = compsData?.reason === 'provider-not-configured';
  const carfaxUrl = `https://www.carfaxcanada.ca/report/?vin=${vin}`;
  const cpicUrl = `https://www.rcmp-grc.gc.ca/en/cpic/online-queries`;

  return (
    <div style={s.wrap}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.headerLeft}>
          <span>🇨🇦</span>
          <p style={s.headerTitle}>CMV — Canadian Market Value</p>
        </div>
        <span style={val ? s.liveBadge : s.pendingBadge}>{val ? 'Live Data' : 'Pending'}</span>
      </div>

      <div style={s.body}>
        {loading && <p style={s.loading}>Loading market data...</p>}

        {/* Pricing tiers */}
        {val ? (
          <>
            <div style={s.pricingGrid}>
              <div style={s.priceCardHighlight}>
                <div style={s.priceLabel}>Private Sale (Avg)</div>
                <div style={s.priceValueGreen}>{fmt(val.retail_private_avg_cad)}</div>
                <div style={s.priceRange}>
                  {fmt(val.retail_private_low_cad)} – {fmt(val.retail_private_high_cad)}
                </div>
              </div>
              <div style={s.priceCard}>
                <div style={s.priceLabel}>Dealer Retail Asking</div>
                <div style={s.priceValue}>{fmt(val.dealer_retail_cad)}</div>
                <div style={s.priceRange}>Typical lot price</div>
              </div>
              <div style={s.priceCard}>
                <div style={s.priceLabel}>Wholesale / Auction</div>
                <div style={s.priceValue}>{fmt(val.wholesale_auction_cad)}</div>
                <div style={s.priceRange}>Dealer-to-dealer price</div>
              </div>
              <div style={s.priceCard}>
                <div style={s.priceLabel}>Trade-In Estimate</div>
                <div style={s.priceValue}>{fmt(val.trade_in_est_cad)}</div>
                <div style={s.priceRange}>What a dealer will offer you</div>
              </div>
            </div>

            {/* Market intelligence chips */}
            {intel && (
              <>
                <div style={s.intelRow}>
                  {intel.demand && (
                    <span style={demandStyle(intel.demand)}>Demand: {intel.demand}</span>
                  )}
                  {intel.active_listings && (
                    <span style={s.intelChip}>{intel.active_listings} active listings</span>
                  )}
                  {intel.days_on_market_avg && (
                    <span style={s.intelChip}>Avg {intel.days_on_market_avg} days to sell</span>
                  )}
                  {intel.sold_last_90_days && (
                    <span style={s.intelChip}>{intel.sold_last_90_days} sold (90 days)</span>
                  )}
                </div>
                {intel.market_summary && <p style={s.summary}>"{intel.market_summary}"</p>}
              </>
            )}
            <div style={s.divider} />
          </>
        ) : unavailable ? (
          // Previously: "Live pricing data coming soon — market intelligence is
          // being activated." Nothing was being activated; a key was missing,
          // and the sentence would have stayed untrue indefinitely.
          <p style={s.pending}>
            {notConfigured
              ? 'Live market pricing is not available for this listing right now.'
              : 'Live market pricing is temporarily unavailable. Please check back shortly.'}
          </p>
        ) : null}

        {/* CARFAX Canada */}
        <div style={s.carfaxWrap}>
          <div style={s.carfaxLeft}>
            <div style={s.carfaxTitle}>🔍 Full Vehicle History Report</div>
            <div style={s.carfaxSub}>Accidents · Liens · Odometer · Ownership history</div>
          </div>
          <a href={carfaxUrl} target="_blank" rel="noopener noreferrer" style={s.carfaxBtn}>
            Get CARFAX Canada Report
          </a>
        </div>

        {/* Stolen vehicle check */}
        <div style={s.stolenWrap}>
          <div style={s.carfaxLeft}>
            <div style={s.carfaxTitle}>🚨 Stolen Vehicle Check</div>
            <div style={s.carfaxSub}>VIN: {vin} · Cross-referenced against national records</div>
          </div>
          <a href={cpicUrl} target="_blank" rel="noopener noreferrer" style={s.stolenBtn}>
            Check RCMP Database
          </a>
        </div>

        <p style={s.poweredBy}>Powered by CMV — Canadian Market Value + AI Market Intelligence</p>
      </div>
    </div>
  );
};

export default MarketValueBadge;
